import { useState } from 'react'
import { Link, useParams, useNavigate } from 'react-router-dom'
import { PageHeader, Breadcrumb } from '@/components/layout'
import {
  Card,
  CardContent,
  Button,
  Input,
  Textarea,
  Label,
  Alert,
} from '@/components/ui'
import { api, ApiError } from '@/api/client'

export function CreateAppPage() {
  const { orgSlug } = useParams()
  const navigate = useNavigate()
  const [name, setName] = useState('')
  const [slug, setSlug] = useState('')
  const [description, setDescription] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({})

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!orgSlug || !name || !slug) return

    setIsLoading(true)
    setError(null)
    setFieldErrors({})

    try {
      const response = await api.apps.create(orgSlug, {
        slug,
        name,
        description: description || undefined,
      })

      // Navigate to the new app's overview page
      navigate(`/orgs/${orgSlug}/apps/${response.app.slug}`)
    } catch (err) {
      console.error('Failed to create app:', err)
      if (err instanceof ApiError) {
        // Check for validation errors
        if (err.validationErrors && err.validationErrors.length > 0) {
          setFieldErrors(err.getFieldErrors())
          setError(err.getValidationSummary())
        } else {
          setError(err.message)
        }
      } else {
        setError(err instanceof Error ? err.message : 'Failed to create application')
      }
    } finally {
      setIsLoading(false)
    }
  }

  const generateSlug = (name: string) => {
    return name
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '')
  }

  return (
    <div>
      <PageHeader
        title="Create App"
        description="Set up a new application"
        breadcrumb={
          <Breadcrumb
            items={[
              { label: 'Apps', href: `/orgs/${orgSlug}/apps` },
              { label: 'Create' },
            ]}
          />
        }
      />

      <Card className="max-w-2xl">
        <CardContent className="pt-6">
          {error && (
            <Alert variant="error" className="mb-4" onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            <Input
              label="App Name"
              placeholder="My Awesome App"
              value={name}
              onChange={(e) => {
                setName(e.target.value)
                if (!slug) {
                  setSlug(generateSlug(e.target.value))
                }
                // Clear field error on change
                if (fieldErrors.name) {
                  setFieldErrors(prev => ({ ...prev, name: '' }))
                }
              }}
              hint={!fieldErrors.name ? "A friendly name for your app" : undefined}
              error={fieldErrors.name}
              required
              disabled={isLoading}
            />

            <Input
              label="App ID (Slug)"
              placeholder="my-awesome-app"
              value={slug}
              onChange={(e) => {
                setSlug(e.target.value)
                // Clear field error on change
                if (fieldErrors.slug) {
                  setFieldErrors(prev => ({ ...prev, slug: '' }))
                }
              }}
              hint={!fieldErrors.slug ? "URL-safe identifier (lowercase, no spaces). This will be your client_id." : undefined}
              error={fieldErrors.slug}
              required
              disabled={isLoading}
            />

            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                placeholder="Describe what your app does..."
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={3}
                disabled={isLoading}
              />
              <p className="text-xs text-[var(--text-muted)]">
                Optional. Helps team members understand the app's purpose.
              </p>
            </div>

            <div className="flex items-center gap-3 pt-4">
              <Button type="submit" loading={isLoading}>
                Create App
              </Button>
              <Button type="button" variant="secondary" asChild disabled={isLoading}>
                <Link to={`/orgs/${orgSlug}/apps`}>Cancel</Link>
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  )
}
